import {
  inlineRelumeAndFinsweetVariables,
  inlineVariableValues,
} from './convert_variables';
import relumeStylesV21Raw from './relume_stylesv2.1.json';
import relumeStylesV2Raw from './relume_stylesv2.json';
import relumeVariables from './relume_variables.json';
import { Component, ComponentStyle, WfVariable } from './schema';

export const V21ToV22VariableMap = new Map([
  [
    '@var_relume-variable--neutral-white',
    '@var_relume-variable-color-neutral-2',
  ],
  [
    '@var_relume-variable--neutral-shade-7',
    '@var_relume-variable-color-neutral-1',
  ],
  [
    '@var_relume-variable--color-scheme-1-text',
    '@var_relume-variable-text-color-1',
  ],
  ['@var_relume-variable--transparent', 'transparent'],
  [
    '@var_relume-variable--color-scheme-1-background',
    '@var_relume-variable-background-color-1',
  ],
  [
    '@var_relume-variable--color-scheme-1-border',
    '@var_relume-variable-border-color-1',
  ],
  [
    '@var_relume-variable--color-scheme-1-foreground',
    '@var_relume-variable--neutral-white',
  ],
]);

export function convertRelumeCFVersion(
  component: Component,
  version: '2' | '2.1' | '3',
) {
  /*
  This function converts a component to the specified client first version.
  */
  if (version === '3') {
    // The way we are applying the color scheme is by adding a utility class to
    // the section, it also includes css glue to override the scheme variables
    // so that nested elements will use associated color scheme.
    //
    // Due to how Webflow style inheritance works, because we are adding a
    // utility class style to the element, we need to remove properties that
    // are overridden (text and background).
    //
    // We want to look into removing these from the components themselves.
    //
    // NOTE we only want to apply this to the root class
    if (component.payload.styles.length > 0) {
      let newStyle = JSON.stringify(component.payload.styles[0]);
      const variableIdsToRemove = [
        'relume-variable--color-scheme-1-background',
        'relume-variable--color-scheme-1-text',
      ];
      for (const variableId of variableIdsToRemove) {
        const regex = new RegExp(
          `\\s*[\\w-]+\\s*:\\s*@var_${variableId}\\s*;?`,
          'g',
        );
        newStyle = newStyle.replace(regex, '');
      }
      component.payload.styles[0] = JSON.parse(newStyle);
    }
    return component;
  }
  if (version === '2') {
    // version 2 has no variables
    component = inlineRelumeAndFinsweetVariables(component);

    let styleNameToV2StyleMap = new Map(
      relumeStylesV2Raw.map((style) => [style.data.name, style]),
    );

    // stored as [v1, v2]
    // each change is a mapped class name
    const v1ToV2ClassNameChanges = [
      ['form-block', 'form_component'],
      ['form', 'form_form'],
      ['form-input', 'form_input'],
      ['form-field-label', 'form_field-label'],
      ['form-checkbox', 'form_checkbox'],
      ['form-checkbox-icon', 'form_checkbox-icon'],
      ['form-checkbox-label', 'form_checkbox-label'],
      ['form-radio', 'form_radio'],
      ['form-radio-icon', 'form_radio-icon'],
      ['form-radio-label', 'form_radio-label'],
    ];

    for (let [v1, v2] of v1ToV2ClassNameChanges) {
      let newStyles = [];
      for (let style of component.payload.styles) {
        if (style.name === v2) {
          style.name = v1;
        }
        // this is required as some classes have slightly changed and we no longer use swatches
        let v2Style = styleNameToV2StyleMap.get(style.name);
        if (
          v2Style &&
          !style.name.includes('margin') &&
          !style.name.includes('padding')
        ) {
          style.styleLess = v2Style.data.styleLess;
        }
        newStyles.push(style);
      }

      component.payload.styles = newStyles;
    }
  } else if (version === '2.1') {
    const styleMap = new Map(component.payload.styles.map((s) => [s._id, s]));
    const styleIdToSelectorMap = new Map();
    function populateAllChildSelectors(
      style: ComponentStyle,
      selector: string,
    ) {
      const maybeSelector = styleIdToSelectorMap.get(style._id);
      if (maybeSelector && maybeSelector.length >= selector.length) {
        return;
      }
      for (let childId of style.children) {
        let child = styleMap.get(childId);
        if (!child) {
          // this is a common and safe case
          continue;
        }
        const newSelector = `${selector}.${child.name}`;
        populateAllChildSelectors(child, newSelector);
      }
      styleIdToSelectorMap.set(style._id, selector);
    }
    for (let style of component.payload.styles) {
      populateAllChildSelectors(style, style.name);
    }

    let styleNameToV21StyleMap = new Map(
      relumeStylesV21Raw.map((style) => [style.data.name, style]),
    );

    let newStyles = [];
    for (let style of component.payload.styles) {
      let v2Style = styleNameToV21StyleMap.get(
        styleIdToSelectorMap.get(style._id),
      );
      if (
        v2Style &&
        !style.name.includes('container') &&
        !style.name.includes('margin') &&
        !style.name.includes('padding')
      ) {
        style.styleLess = v2Style.data.styleLess;
        if (v2Style.data.variants && style.variants) {
          for (let [key, value] of Object.entries(v2Style.data.variants)) {
            if (style.variants[key]) {
              style.variants[key].styleLess = value.styleLess;
            }
          }
        }
      }
      newStyles.push(style);
    }

    // convert some variables to old variables. The component team gave us these mappings
    let styleText = JSON.stringify(newStyles);
    for (let [oldVar, newVar] of V21ToV22VariableMap) {
      styleText = styleText.replace(new RegExp(oldVar, 'g'), newVar);
    }

    component.payload.styles = JSON.parse(styleText);
    component = mapVariablesToV21(component);
  }
  return component;
}

/**
 * We've added new variables to sections in the lead up to supporting SGB,
 * however the existing cloneable project does not have these variables.
 *
 * This function replaces the new variables with the old ones.
 */
export function mapVariablesToV21(component: Component): Component {
  const missingVariableIds = new Set([
    'relume-variable--neutral-white-10',
    'relume-variable--neutral-white',
    'relume-variable--neutral-shade-1',
    'relume-variable--neutral-shade-2',
    'relume-variable--neutral-shade-3',
    'relume-variable--neutral-shade-4',
    'relume-variable--neutral-shade-5',
    'relume-variable--neutral-shade-6',
    'relume-variable--neutral-shade-7',
    'relume-variable--neutral-shade-7-10',
    'relume-variable--transparent',
    'relume-variable--chromatic1-shade-1',
    'relume-variable--chromatic1-shade-2',
    'relume-variable--chromatic1-shade-3',
    'relume-variable--chromatic1-shade-4',
    'relume-variable--chromatic1-shade-5',
    'relume-variable--chromatic1-shade-6',
    'relume-variable--chromatic1-shade-7',
    'relume-variable--chromatic2-shade-1',
    'relume-variable--chromatic2-shade-2',
    'relume-variable--chromatic2-shade-3',
    'relume-variable--chromatic2-shade-4',
    'relume-variable--chromatic2-shade-5',
    'relume-variable--chromatic2-shade-6',
    'relume-variable--chromatic2-shade-7',
    'relume-variable--chromatic3-shade-1',
    'relume-variable--chromatic3-shade-2',
    'relume-variable--chromatic3-shade-3',
    'relume-variable--chromatic3-shade-4',
    'relume-variable--chromatic3-shade-5',
    'relume-variable--chromatic3-shade-6',
    'relume-variable--chromatic3-shade-7',
    'relume-variable--chromatic4-shade-1',
    'relume-variable--chromatic4-shade-2',
    'relume-variable--chromatic4-shade-3',
    'relume-variable--chromatic4-shade-4',
    'relume-variable--chromatic4-shade-5',
    'relume-variable--chromatic4-shade-6',
    'relume-variable--chromatic4-shade-7',
    'relume-variable--chromatic5-shade-1',
    'relume-variable--chromatic5-shade-2',
    'relume-variable--chromatic5-shade-3',
    'relume-variable--chromatic5-shade-4',
    'relume-variable--chromatic5-shade-5',
    'relume-variable--chromatic5-shade-6',
    'relume-variable--chromatic5-shade-7',
    'relume-variable--color-scheme-1-text',
    'relume-variable--color-scheme-1-background',
    'relume-variable--color-scheme-1-foreground',
    'relume-variable--color-scheme-1-border',
    'relume-variable--color-scheme-1-accent',
    'relume-variable--color-scheme-2-text',
    'relume-variable--color-scheme-2-background',
    'relume-variable--color-scheme-2-foreground',
    'relume-variable--color-scheme-2-border',
    'relume-variable--color-scheme-2-accent',
    'relume-variable--color-scheme-3-text',
    'relume-variable--color-scheme-3-background',
    'relume-variable--color-scheme-3-foreground',
    'relume-variable--color-scheme-3-border',
    'relume-variable--color-scheme-3-accent',
    'relume-variable--color-scheme-4-text',
    'relume-variable--color-scheme-4-background',
    'relume-variable--color-scheme-4-foreground',
    'relume-variable--color-scheme-4-border',
    'relume-variable--color-scheme-4-accent',
    'relume-variable--color-scheme-5-text',
    'relume-variable--color-scheme-5-background',
    'relume-variable--color-scheme-5-foreground',
    'relume-variable--color-scheme-5-border',
    'relume-variable--color-scheme-5-accent',
    'relume-variable--color-scheme-6-text',
    'relume-variable--color-scheme-6-background',
    'relume-variable--color-scheme-6-foreground',
    'relume-variable--color-scheme-6-border',
    'relume-variable--color-scheme-6-accent',
    'relume-variable--color-scheme-7-text',
    'relume-variable--color-scheme-7-background',
    'relume-variable--color-scheme-7-foreground',
    'relume-variable--color-scheme-7-border',
    'relume-variable--color-scheme-7-accent',
    'relume-variable--color-scheme-8-text',
    'relume-variable--color-scheme-8-background',
    'relume-variable--color-scheme-8-foreground',
    'relume-variable--color-scheme-8-border',
    'relume-variable--color-scheme-8-accent',
    'relume-variable--color-scheme-9-text',
    'relume-variable--color-scheme-9-background',
    'relume-variable--color-scheme-9-foreground',
    'relume-variable--color-scheme-9-border',
    'relume-variable--color-scheme-9-accent',
    'relume-variable--color-scheme-10-text',
    'relume-variable--color-scheme-10-background',
    'relume-variable--color-scheme-10-foreground',
    'relume-variable--color-scheme-10-border',
    'relume-variable--color-scheme-10-accent',
    'relume-variable--color-scheme-11-text',
    'relume-variable--color-scheme-11-background',
    'relume-variable--color-scheme-11-foreground',
    'relume-variable--color-scheme-11-border',
    'relume-variable--color-scheme-11-accent',
    'relume-variable--color-scheme-12-text',
    'relume-variable--color-scheme-12-background',
    'relume-variable--color-scheme-12-foreground',
    'relume-variable--color-scheme-12-border',
    'relume-variable--color-scheme-12-accent',
    'relume-variable--radius-small',
    'relume-variable--radius-medium',
    'relume-variable--radius-large',
    'relume-variable--radius-xlarge',
    'relume-variable--radius-none',
    'relume-variable--font-style-heading',
    'relume-variable--font-style-body',
    'relume-variable--border-width',
    'relume-variable--divider-width',
  ]);
  const missingVariables = relumeVariables.filter((variable) =>
    missingVariableIds.has(variable.id),
  ) as WfVariable[];
  return inlineVariableValues(component, missingVariables);
}
